// =============================================================================
// components/ComingSoonScreen.tsx — the "Coming Soon — CRYB v2" gate screen.
//
// v1 POLICY (user direction): every crypto/web3 surface (wallet, NFTs, trading,
// swap, signals, DeFi) is VISIBLE and reachable — to build FOMO into v2 — but
// perfectly blocked. This screen is what every gated route renders: a polished
// glass card (lock glyph on the brand gradient, sparkle badge, short FOMO copy)
// with a "Notify me" CTA that only confirms locally. No crypto functionality is
// exposed and no money endpoint is called (App Store 3.1.1 / 3.1.5 safe).
//
// Usage — as a navigator component, mirroring makePlaceholder():
//   <Stack.Screen name="Swap" component={makeComingSoon('Swap', 'Instant token swaps, right inside CRYB.')} />
// or directly:
//   <ComingSoonScreen feature="Wallet" tagline="Your keys, your coins." />
//
// Built entirely on the glass contract + theme tokens. No new hues, no wagmi,
// nothing on the boot graph.
// =============================================================================
import React, { useRef, useState } from 'react';
import { View, Text, StyleSheet, Animated, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons } from './ui/Icon';
import BackgroundBlobs from './ui/BackgroundBlobs';
import { GlassCard, GlassButton, glassTokens } from './glass';
import ComingSoonBadge from './ComingSoonBadge';
import { colors, spacing, fonts, radius } from '../constants/theme';

const DEFAULT_TAGLINE = 'Trading signals, wallets & NFTs land in CRYB v2.';

export interface ComingSoonScreenProps {
  /** Feature name shown as the card title (e.g. "Wallet", "Swap"). */
  feature?: string;
  /** One-line FOMO hook specific to the feature. */
  tagline?: string;
}

export default function ComingSoonScreen({
  feature = 'This feature',
  tagline = DEFAULT_TAGLINE,
}: ComingSoonScreenProps) {
  const insets = useSafeAreaInsets();
  const [notified, setNotified] = useState(false);
  // Gentle confirmation pop when "Notify me" flips to the success pill.
  const pop = useRef(new Animated.Value(1)).current;

  function handleNotify() {
    if (notified) return;
    setNotified(true);
    pop.setValue(0.85);
    Animated.spring(pop, { toValue: 1, useNativeDriver: true, speed: 30, bounciness: 10 }).start();
  }

  return (
    <View style={styles.fill}>
      <BackgroundBlobs />
      <ScrollView
        style={styles.fill}
        contentContainerStyle={[
          styles.center,
          { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.xxl },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <GlassCard style={styles.card} contentStyle={styles.cardContent} radius={glassTokens.radius.xl}>
          {/* Gradient lock glyph with a sparkle accent — locked, not broken. */}
          <View style={styles.glyphWrap}>
            <LinearGradient
              colors={colors.gradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.glyph}
            >
              <Ionicons name="lock-closed" size={30} color={colors.white} />
            </LinearGradient>
            <View style={styles.sparkle}>
              <Ionicons name="sparkles" size={14} color={colors.primary} />
            </View>
          </View>

          <ComingSoonBadge label="Coming Soon · CRYB v2" />

          <Text style={styles.title}>{feature}</Text>
          <Text style={styles.tagline}>{tagline}</Text>
          {tagline !== DEFAULT_TAGLINE ? (
            <Text style={styles.sub}>{DEFAULT_TAGLINE}</Text>
          ) : null}

          <Animated.View style={{ transform: [{ scale: pop }], alignSelf: 'stretch' }}>
            {notified ? (
              <View style={styles.notifiedPill} accessibilityRole="text" accessibilityLabel="You're on the list">
                <Ionicons name="checkmark-circle" size={18} color={colors.success} />
                <Text style={styles.notifiedText}>You’re on the list — see you in v2</Text>
              </View>
            ) : (
              <GlassButton
                title="Notify me"
                icon="notifications-outline"
                variant="primary"
                size="lg"
                fullWidth
                onPress={handleNotify}
              />
            )}
          </Animated.View>

          <Text style={styles.footnote}>Early CRYB members get first access.</Text>
        </GlassCard>
      </ScrollView>
    </View>
  );
}

/**
 * Factory mirroring makePlaceholder(): returns a screen component for a
 * navigator registration. Keeps CryptoStack / App.tsx wiring one-liners.
 */
export function makeComingSoon(feature: string, tagline?: string) {
  function ComingSoonRoute() {
    return <ComingSoonScreen feature={feature} tagline={tagline} />;
  }
  ComingSoonRoute.displayName = `ComingSoon(${feature})`;
  return ComingSoonRoute;
}

const styles = StyleSheet.create({
  fill: { flex: 1, backgroundColor: colors.bg },
  center: { flexGrow: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl },
  card: { width: '100%', maxWidth: 420 },
  cardContent: {
    alignItems: 'center',
    paddingVertical: spacing.xxxl,
    paddingHorizontal: spacing.xxl,
    gap: spacing.md,
  },
  glyphWrap: { marginBottom: spacing.xs },
  glyph: {
    width: 72,
    height: 72,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: colors.primary,
    shadowOpacity: 0.35,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  sparkle: {
    position: 'absolute',
    top: -4,
    right: -8,
    width: 26,
    height: 26,
    borderRadius: radius.full,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: glassTokens.borderSubtle,
  },
  title: { ...fonts.extrabold, color: colors.ink, fontSize: 28, textAlign: 'center' },
  tagline: { ...fonts.semibold, color: colors.ink2, fontSize: 15, textAlign: 'center', lineHeight: 21 },
  sub: { ...fonts.medium, color: colors.muted, fontSize: 13, textAlign: 'center', lineHeight: 19 },
  notifiedPill: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    height: 54,
    borderRadius: glassTokens.radius.pill,
    backgroundColor: 'rgba(45,181,93,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(45,181,93,0.25)',
  },
  notifiedText: { ...fonts.semibold, color: colors.success, fontSize: 15 },
  footnote: { ...fonts.medium, color: colors.faint, fontSize: 12, marginTop: spacing.xs },
});
